import { motion, type Variants } from 'framer-motion';
import { memo, useState } from 'react';
import type { BundledLanguage } from 'shiki';
import { CodeBlock } from './CodeBlock';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import type { FileSelection } from '~/types/chat';
import { classNames } from '~/utils/classNames';
import { WORK_DIR } from '~/utils/constants';
import { cubicEasingFn } from '~/utils/easings';

const transition = {
  duration: 0.2,
  ease: cubicEasingFn,
};

const codeBlockVariants = {
  closed: {
    height: 0,
    opacity: 0,
    transition,
  },
  open: {
    height: 'auto',
    opacity: 1,
    transition,
  },
} satisfies Variants;

interface FileSelectionsProps {
  className?: string;
  itemClassName?: string;
  buttonClassName?: string;
  allowDelete?: boolean;
  fileSelections?: FileSelection[];
  onFileSelectionDelete?: (index: number) => void;
}

export const FileSelections = memo(
  ({
    className,
    itemClassName,
    buttonClassName,
    allowDelete,
    fileSelections = [],
    onFileSelectionDelete,
  }: FileSelectionsProps) => {
    const [openCodeBlocks, setOpenCodeBlocks] = useState(new Set<number>());

    const toggleCodeBlock = (index: number) => {
      const newSet = new Set(openCodeBlocks);

      if (openCodeBlocks.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }

      setOpenCodeBlocks(newSet);
    };

    if (fileSelections.length === 0) {
      return null;
    }

    return (
      <>
        <div className={classNames('file-selections', className)}>
          <div className="flex flex-col space-y-2">
            {fileSelections.map(({ filePath, text }, index) => {
              const extension = filePath.split('.').at(-1);
              const isCodeBlockOpen = openCodeBlocks.has(index);

              return (
                <div key={index} className="border border-bolt-elements-borderColor rounded-lg overflow-hidden">
                  <div className={classNames('flex items-center pr-1', itemClassName)}>
                    <button
                      className={classNames('bg-transparent w-full flex items-center', buttonClassName)}
                      onClick={() => toggleCodeBlock(index)}
                    >
                      <div className="flex-1 flex items-center gap-1 text-left text-sm px-2 py-1">
                        <div className="i-ph:file-duotone" />
                        {filePath.replace(`${WORK_DIR}/`, '')}
                      </div>
                      <span
                        className={classNames('cursor-pointer', {
                          'i-ph:caret-down': isCodeBlockOpen,
                          'i-ph:caret-right': !isCodeBlockOpen,
                        })}
                      />
                    </button>
                    {allowDelete && (
                      <LegacyIconButton
                        className="ml-0.5"
                        icon="i-ph:x"
                        size="sm"
                        onClick={() => onFileSelectionDelete?.(index)}
                      />
                    )}
                  </div>
                  <motion.div
                    initial="closed"
                    animate={isCodeBlockOpen ? 'open' : 'closed'}
                    exit="closed"
                    variants={codeBlockVariants}
                    className="text-xs"
                  >
                    <div className="h-[1px] bg-bolt-elements-borderColor" />
                    <div className="p-4 bg-bolt-elements-messages-code-background max-h-[250px] overflow-y-auto">
                      <CodeBlock code={text} language={extension as BundledLanguage} disableCopy />
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  },
);
